<template>
  <div v-if="loaded" class="min-h-screen lg:pr-28">
    <!-- Header -->
    <div
      class="bg-expo_dblue h-120 w-full relative z-10 text-white flex flex-col justify-end items-center pb-12 overflow-y-visible hero-gradient"
    >
      <div
        v-if="event.image"
        class="z-0 absolute top-0 left-0 right-0 bottom-0 w-full h-full bg-cover bg-no-repeat bg-center"
        :style="{ backgroundImage: 'url(' + event.image + ')' }"
      ></div>
      <div
        v-if="event.image"
        class="opacity-25 bg-expo_dblue z-0 absolute top-0 left-0 right-0 bottom-0 w-full h-full"
      ></div>

      <div
        class="z-10 w-full flex flex-col pb-24 lg:pb-12 space-y-2 text-center"
      >
        <h3 class="text-expo_red font-light uppercase m-0">NZ CAREERS EXPO</h3>
        <h1 class="m-0">{{ event.title }}</h1>
      </div>

      <button
        href="/"
        class="absolute bottom-0 transform -translate-y-8 lg:translate-y-6 btn btn-expo-red z-20 m-0"
        @click="register('ExhibitorRegistration')"
      >
        Join as an exhibitor
      </button>
    </div>

    <!-- Map -->
    <div
      v-if="event.map"
      class="bg-white lg:h-120 h-screen w-full overflow-y-visible relative"
    >
      <iframe
        :src="event.map"
        class="absolute w-full h-full top-0 left-0 right-0 bottom-0"
        style="border: 0"
        allowfullscreen=""
        loading="lazy"
      ></iframe>
      <h3
        class="absolute top-24 right-6 lg:top-12 lg:right-12 rounded-xl bg-black text-white font-3xl font-normal p-6"
      >
        {{ event.date_range }}
      </h3>
    </div>

    <!-- EVENT INFORMATION -->
    <div
      class="bg-white w-full max-w-6xl m-auto relative justify-center items-start py-12 px-6 lg:px-0 overflow-y-visible grid grid-cols-1 lg:grid-cols-2 gap-12"
    >
      <!-- col 1 -->
      <div class="w-full flex flex-col justify-center items-start">
        <a
          class="flex items-center space-x-2 mb-8 text-expo_red no-underline"
          href="#"
          @click="register('Home')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          <span>Back</span>
        </a>
        <h2 class="font-3xl font-light w-full text-center lg:text-left">
          {{ event.title }} {{ event.year }}
        </h2>
        <div
          v-if="event.venue"
          class="w-full flex space-x-2 mt-2 justify-center lg:justify-start"
        >
          <div class="flex-initial w-5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-gray-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <p>{{ event.venue }}</p>
        </div>
        <div
          v-if="event.schedule"
          class="w-full flex space-x-2 mt-2 justify-center lg:justify-start"
        >
          <div class="flex-initial w-5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-gray-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="wysiwyg_html" v-html="event.schedule"></div>
        </div>
        <div class="w-full block lg:flex lg:space-x-2 mt-8">
          <button
            v-if="event.floor_plan"
            class="btn btn-expo-red mx-auto lg:mx-0"
            @click="showFloorPlanModal"
          >
            Floorplan
          </button>
        </div>
      </div>

      <!-- col 2 -->
      <div class="w-full flex flex-col justify-center items-start">
        <EventsSchedule />
        <div
          class="w-full block lg:flex justify-end lg:space-x-2 mt-8 lg:mt-4 lg:pr-4"
        >
          <button
            class="btn btn-black h-8 px-3 py-4 mx-auto lg:mx-0"
            @click="register('ExhibitorRegistration')"
          >
            Register as an exhibitor
          </button>
          <button
            class="btn btn-trans-black h-8 px-3 py-4 mx-auto lg:mx-0"
            @click="register('SchoolRegistration')"
          >
            Register your school's attendance
          </button>
        </div>
      </div>
    </div>

    <!-- EVENT GALLERY -->
    <div class="bg-gray-50 relative w-full" style="height: 39rem">
      <div class="w-full" style="height: 39rem">
        <div
          v-for="(item, i) in gallery"
          :key="i"
          class="cursor-pointer float-left bg-norepeat h-52 relative"
          :class="[item.size === 2 ? 'w-1/2' : 'w-1/4']"
          @click="showGalleryModal(i)"
        >
          <div
            class="absolute w-full h-52 bg-cover bg-no-repeat bg-center"
            :style="{ backgroundImage: 'url(' + item.img + ')' }"
          ></div>
          <div
            class="absolute w-full h-52 flex overflow-x-auto border-4 border-white"
          >
            <div
              class="flex-shrink-0 backdrop-filter backdrop-grayscale w-full hover:opacity-0 transition duration-300 ease-in-out"
            ></div>
          </div>
        </div>
      </div>
      <a
        target="_blank"
        :href="settings.cloud_url + '?event=' + event.id"
        class="z-10 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-24 text-center lg:h-12 btn btn-expo-red no-underline"
        >View {{ event.title }} Exhibitors</a
      >
    </div>

    <!-- UPCOMING EVENTS -->
    <div class="py-32 w-full relative px-6 lg:px-0">
      <div class="w-full absolute bottom-0 h-1/2 bg-gray-100"></div>
      <div class="w-full max-w-6xl m-auto">
        <h2
          class="text-light text-light font-light text-left mb-12 text-center lg:text-left"
        >
          Upcoming Expos
        </h2>
        <EventsPanel v-if="events.length > 0" />
      </div>
    </div>

    <!-- Gallery Modal -->
    <div
      v-show="floorModal"
      id="floorModal"
      class="fixed z-50 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >
        <!-- Modal panel, show/hide based on modal state. -->
        <div
          style="height: calc(100vh - 10rem)"
          :class="floorModal ? '' : 'p-8 w-52 h-52'"
          class="bg-cover inline-block align-bottom bg-white border-8 border-white rounded-4xl p-6 text-left shadow transform transition-all duration-300 ease-in-out sm:my-8 sm:align-middle sm:max-w-4xl sm:max-h-lg w-full sm:p-16 relative top-12"
        >
          <div
            class="flex flex-col bg-black absolute bg-center inset-0 w-full h-full rounded-4xl overflow-hidden z-10"
          >
            <img v-if="floorModal" id="printable_image" :src="floorModal" />
          </div>
          <div class="flex flex-col space-y-4 text-expo_red">
            <button
              type="button"
              class="absolute z-40 right-16 top-12 transform hover:rotate-180 transition-all duration-300 text-expo_red hover:text-expo_red"
              @click="floorModal = false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <svg
              id="print"
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6 absolute bottom-8 right-16 z-10 cursor-pointer"
              viewBox="0 0 20 20"
              fill="currentColor"
              @click="print"
            >
              <path
                fill-rule="evenodd"
                d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
    <!-- modal -->

    <!-- Gallery Modal -->
    <div
      v-show="galleryModal"
      id="galleryModal"
      class="fixed z-50 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >
        <!-- Modal panel, show/hide based on modal state. -->
        <div
          style="height: calc(100vh - 10rem)"
          :class="galleryModal ? '' : 'p-8 w-52 h-52'"
          class="bg-cover inline-block align-bottom bg-white border-8 border-white rounded-4xl p-6 text-left shadow transform transition-all duration-300 ease-in-out sm:my-8 sm:align-middle sm:max-w-4xl sm:max-h-lg w-full sm:p-16 relative top-12"
        >
          <div
            class="flex flex-col bg-black absolute bg-center inset-0 w-full h-full rounded-4xl overflow-hidden z-10 bg-no-repeat"
          >
            <img v-if="galleryModal" :src="galleryModal" />
          </div>
          <div class="flex flex-col space-y-4">
            <button
              type="button"
              class="absolute z-40 right-16 top-12 transform hover:rotate-180 transition-all duration-300 text-white hover:text-expo_red"
              @click="galleryModal = false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- modal -->
  </div>
</template>

<script>
import { shared } from '../../../js/mixins/shared.js'
import { EventBus } from '../../../js/plugins/event-bus.js'
import EventsPanel from '../components/EventsPanel.vue'
import EventsSchedule from '../components/EventsSchedule.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {
    EventsPanel,
    EventsSchedule,
  },

  mixins: [shared],

  props: {
    slug: {
      default: '',
    },
  },

  data() {
    return {
      galleryModal: false,
      floorModal: false,
      gallery: [
        { img: '/img/gallery/careers-expo-18.jpeg', size: 2 },
        { img: '/img/gallery/careers-expo-19.jpeg', size: 2 },
        { img: '/img/gallery/careers-expo-20.jpeg', size: 4 },
        { img: '/img/gallery/careers-expo-21.jpeg', size: 2 },
        { img: '/img/gallery/careers-expo-22.jpeg', size: 4 },
        { img: '/img/gallery/careers-expo-23.jpeg', size: 2 },
        { img: '/img/gallery/careers-expo-24.jpeg', size: 2 },
      ],

      event: {},
      loaded: false,
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
      events: 'expo/getEvents',
    }),
  },

  watch: {
    'galleryModal': {
      handler: function (str) {
        let active = false
        if (str.length > 0) {
          active = true
        }
        EventBus.$emit('modal_state_changing', active)
      },

      deep: true,
    },

    'floorModal': {
      handler: function (str) {
        let active = false
        if (str.length > 0) {
          active = true
        }
        EventBus.$emit('modal_state_changing', active)
      },

      deep: true,
    },

    '$route.params.slug'(newSlug) {
      this.$set(this, 'loaded', true)
      this.getEvent(newSlug)
    },
  },

  async mounted() {
    if (this.slug) {
      this.getEvent(this.slug)
    }

    if (this.events.length < 1) {
      await this.dispatchSetEvents()
    }
  },

  methods: {
    ...mapActions({
      dispatchSetEvents: 'expo/dispatchSetEvents',
    }),

    showGalleryModal(i) {
      if (this.gallery[i]) {
        this.$set(this, 'galleryModal', this.gallery[i].img)
      } else {
        return
      }
    },

    showFloorPlanModal() {
      if (this.event.floor_plan) {
        this.$set(this, 'floorModal', this.event.floor_plan)
      } else {
        return
      }
    },

    getEvent(slug) {
      axios.get('/api/get-event/' + slug).then((response) => {
        if (response.data.event) {
          this.$set(this, 'event', response.data.event)
        }
        this.$set(this, 'loaded', true)
      })
    },

    register(request) {
      this.$router.push({ name: request })
    },

    print() {
      // Create a fake iframe
      const iframe = document.createElement('iframe')

      // Make it hidden
      iframe.style.height = 0
      iframe.style.visibility = 'hidden'
      iframe.style.width = 0

      // Set the iframe's source
      iframe.setAttribute('srcdoc', '<html><body></body></html>')

      document.body.appendChild(iframe)

      iframe.addEventListener('load', function () {
        // Clone the image
        const image = document.getElementById('printable_image').cloneNode()
        image.style.maxWidth = '100%'

        // Append the image to the iframe's body
        const body = iframe.contentDocument.body
        body.style.textAlign = 'center'
        body.appendChild(image)

        image.addEventListener('load', function () {
          // Invoke the print when the image is ready
          iframe.contentWindow.print()
        })
      })
    },
  },
}
</script>

<style scoped>
.wysiwyg_html {
  /* ... */
}
.wysiwyg_html p,
.wysiwyg_html ul {
  margin-bottom: 1rem !important;
}
.wysiwyg_html ul li {
  padding-left: 1rem !important;
}
.wysiwyg_html ul p {
  margin-bottom: 0 !important;
}

.hero-gradient::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
  height: 100%;
  background: rgb(0, 0, 0);
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.804359243697479) 0%,
    rgba(0, 0, 0, 0.5942752100840336) 36%,
    rgba(0, 0, 0, 0) 100%
  );
}
</style>
